import { useRef, useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useSession } from '../store/session';

const CodeMenu = lazy(() => import('../code/CodeMenu'));

interface ModeSelectorProps {
  onOpenEnhance: () => void;
}

export function DirectorSelector() {
  const directorAgent = useSession(s => s.directorAgent);
  const setDirectorAgent = useSession(s => s.setDirectorAgent);
  return (
    <div className="agent-selector chip" style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '0 4px', minHeight: '26px' }}>
      <span 
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '20px' }} 
        title="Chosen Director Agent"
      >
        <ClapboardIcon />
      </span>
      <select
        style={{ fontSize: '10px', paddingRight: '0px', maxWidth: '75px', background: 'transparent', border: 'none', outline: 'none' }}
        value={directorAgent}
        onChange={e => setDirectorAgent(e.target.value as any)}
      >
        <option value="gemini">GEMINI</option>
        <option value="chatgpt">CHATGPT</option>
        <option value="grok">GROK</option>
        <option value="claude">CLAUDE</option>
      </select>
    </div>
  );
}

export default function ModeSelector({ onOpenEnhance }: ModeSelectorProps) {
  const mode = useSession(s => s.mode);
  const setMode = useSession(s => s.setMode);
  const directorAgent = useSession(s => s.directorAgent);
  const setDirectorAgent = useSession(s => s.setDirectorAgent);
  const navigate = useNavigate();
  const codeBtnRef = useRef<HTMLButtonElement>(null);
  const [codeMenuOpen, setCodeMenuOpen] = useState(false);
  const [codeAnchor, setCodeAnchor] = useState<DOMRect | null>(null);
  const [showCodeToast, setShowCodeToast] = useState(false);

  const openCodeMenu = () => {
    setCodeAnchor(codeBtnRef.current?.getBoundingClientRect() ?? null);
    setCodeMenuOpen(true);
  };

  const handlePick = (m: 'chat' | 'view' | 'write') => {
    if (m === 'write') navigate('/code/write');
    else if (m === 'view') navigate('/code/view');
    else {
      // 'chat' — flip the session's mode to 'code' so the next user message
      // is routed through the coding model + preview round-trips into chat.
      setMode('code');
      setShowCodeToast(true);
      setTimeout(() => setShowCodeToast(false), 2500);
    }
  };

  return (
    <div className="row" style={{ gap: 4, display: 'flex', alignItems: 'center' }}>
      {createPortal(
        <AnimatePresence>
          {showCodeToast && (
            <motion.div
              key="code-toast-notification"
              initial={{ opacity: 0, y: 15, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              style={{
                position: 'fixed',
                bottom: '120px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(9, 9, 11, 0.9)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                fontSize: '11px',
                fontWeight: 500,
                padding: '8px 16px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '18px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                zIndex: 2147483647,
                backdropFilter: 'blur(12px)',
                pointerEvents: 'none',
                letterSpacing: '0.05em',
                textTransform: 'uppercase'
              }}
            >
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#a855f7', boxShadow: '0 0 8px #a855f7' }} />
              <span>Coding Agent Ready</span>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <button
        style={{ fontSize: '10px', padding: '4px 8px', minHeight: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        className="chip"
        onClick={onOpenEnhance}
        title="Enhance Prompt (AI)"
      >
        <EnhanceIcon />
      </button>

      <div style={{ width: '1px', height: '14px', background: 'var(--line)', margin: '0 2px' }} />

      <button
        style={{ fontSize: '10px', padding: '4px 8px', minHeight: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        className={`chip ${mode === 'chat' ? 'is-active' : ''}`}
        onClick={() => setMode('chat')}
        title="Chat with Director"
      >
        <ChatBubbleIcon />
      </button>

      <button
        style={{ fontSize: '10px', padding: '4px 8px', minHeight: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        className={`chip ${mode === 'image' ? 'is-active' : ''}`}
        onClick={() => setMode('image')}
        title="Image Generation"
      >
        <ImageIcon />
      </button>

      <button
        style={{ fontSize: '10px', padding: '4px 8px', minHeight: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        className={`chip ${mode === 'video' ? 'is-active' : ''}`}
        onClick={() => setMode('video')}
        title="Video Generation"
      >
        <VideoIcon />
      </button>

      <button
        style={{ fontSize: '10px', padding: '4px 8px', minHeight: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        className={`chip ${mode === 'sound' ? 'is-active' : ''}`}
        onClick={() => setMode('sound')}
        title="Sound Generation"
      >
        <SoundIcon />
      </button>

      <button
        style={{ fontSize: '10px', padding: '4px 8px', minHeight: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        className={`chip ${mode === '3d' ? 'is-active' : ''}`}
        onClick={() => setMode('3d')}
        title="3D Mesh Generation"
      >
        <ThreeDIcon />
      </button>

      <button
        ref={codeBtnRef}
        style={{ fontSize: '10px', padding: '4px 8px', minHeight: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        className={`chip ${mode === 'code' ? 'is-active' : ''}`}
        onClick={openCodeMenu}
        title="Code — chat / view / write"
        aria-haspopup="menu"
        aria-expanded={codeMenuOpen}
      >
        <CodeIcon />
      </button>

      {codeMenuOpen && (
        <Suspense fallback={null}>
          <CodeMenu
            anchorRect={codeAnchor}
            onPick={handlePick}
            onClose={() => setCodeMenuOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
}

function ChatBubbleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

function CodeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m22 8-6 4 6 4V8Z"/>
      <rect width="14" height="12" x="2" y="6" rx="2" ry="2"/>
    </svg>
  );
}

function SoundIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
    </svg>
  );
}

function ThreeDIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  );
}

function EnhanceIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 4 5 5"/>
      <path d="m3 20 14-14"/>
      <path d="m9 3 1 1"/>
      <path d="m15 12 1 1"/>
      <path d="m19 2 1-1"/>
      <path d="m20 10-1-1"/>
    </svg>
  );
}

function ClapboardIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 11h16v8H4z" />
      <path d="m4 11 4-4" />
      <path d="m8 11 4-4" />
      <path d="m12 11 4-4" />
      <path d="m16 11 4-4" />
      <path d="m4 7 16-3" />
    </svg>
  );
}
